/**
 * Section picker (analysis side) — maps a requirement to an artifact section.
 * Searchable artifact→section list; leads with scoped AI suggestions when
 * available (spec §4.5: "even the manual path starts at 90%").
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { FileText } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { listArtifacts, type BidArtifact, type CoverageMappingSuggestion } from '@/lib/api'

export interface SectionPickTarget {
  artifactId: string
  artifactName: string
  sectionKey: string
  sectionTitle: string
}

interface SectionPickerPopoverProps {
  bidId: string
  /** Suggestions scoped to the requirement being mapped (may be empty). */
  suggestions?: CoverageMappingSuggestion[]
  /** Section keys already linked to this requirement — hidden from the list. */
  linkedKeys?: Set<string>
  onSelect: (target: SectionPickTarget) => void
  children: ReactNode
}

interface SectionOption extends SectionPickTarget {
  key: string
}

// Module-level cache: pickers open often, artifacts change rarely.
const artifactCache = new Map<string, Promise<BidArtifact[]>>()

async function loadArtifacts(bidId: string): Promise<BidArtifact[]> {
  let cached = artifactCache.get(bidId)
  if (!cached) {
    cached = listArtifacts(bidId).then((r) => r.artifacts)
    artifactCache.set(bidId, cached)
    // Don't poison the cache on failure
    cached.catch(() => artifactCache.delete(bidId))
  }
  return cached
}

export function invalidateArtifactCache(bidId: string): void {
  artifactCache.delete(bidId)
}

export function SectionPickerPopover({
  bidId,
  suggestions = [],
  linkedKeys,
  onSelect,
  children,
}: SectionPickerPopoverProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [artifacts, setArtifacts] = useState<BidArtifact[] | null>(null)

  useEffect(() => {
    if (!open) return
    let alive = true
    void loadArtifacts(bidId).then((list) => {
      if (alive) setArtifacts(list)
    })
    return () => {
      alive = false
    }
  }, [open, bidId])

  const options = useMemo<SectionOption[]>(() => {
    if (!artifacts) return []
    return artifacts.flatMap((artifact) =>
      (artifact.outlineJson?.sections ?? []).map((section) => ({
        key: `${artifact.id}:${section.sectionKey}`,
        artifactId: artifact.id,
        artifactName: artifact.name,
        sectionKey: section.sectionKey,
        sectionTitle: section.title,
      }))
    )
  }, [artifacts])

  const optionByKey = useMemo(
    () => new Map(options.map((o) => [o.key, o])),
    [options]
  )

  const suggestedOptions = useMemo(
    () =>
      suggestions
        .map((s) => {
          const option = optionByKey.get(`${s.artifactId}:${s.sectionKey}`)
          return option ? { option, reason: s.reason } : null
        })
        .filter((s): s is NonNullable<typeof s> => s !== null)
        .slice(0, 3),
    [suggestions, optionByKey]
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = options
    if (linkedKeys && linkedKeys.size > 0) {
      list = list.filter((o) => !linkedKeys.has(o.key))
    }
    if (q) {
      list = list.filter(
        (o) =>
          o.sectionTitle.toLowerCase().includes(q) || o.artifactName.toLowerCase().includes(q)
      )
    }
    return list
  }, [options, search, linkedKeys])

  // Group by artifact for display
  const grouped = useMemo(() => {
    const groups = new Map<string, { artifactName: string; sections: SectionOption[] }>()
    for (const option of filtered) {
      const group = groups.get(option.artifactId) ?? { artifactName: option.artifactName, sections: [] }
      group.sections.push(option)
      groups.set(option.artifactId, group)
    }
    return [...groups.entries()]
  }, [filtered])

  const pick = (option: SectionOption) => {
    setOpen(false)
    setSearch('')
    onSelect(option)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="border-b border-border p-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sections…"
            className="h-8 text-xs"
            autoFocus
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {artifacts === null ? (
            <p className="px-2 py-3 text-meta">Loading sections…</p>
          ) : options.length === 0 ? (
            <p className="px-2 py-3 text-meta">
              No sections yet — open a response document and generate its outline first.
            </p>
          ) : (
            <>
              {!search && suggestedOptions.length > 0 && (
                <div className="mb-1">
                  <p className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    <GradientSparkleIcon className="h-3 w-3" />
                    Suggested
                  </p>
                  {suggestedOptions.map(({ option, reason }) => (
                    <button
                      key={`suggested-${option.key}`}
                      type="button"
                      onClick={() => pick(option)}
                      className="flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-accent"
                    >
                      <span className="text-xs">{option.sectionTitle}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {option.artifactName} — {reason}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {grouped.map(([artifactId, group]) => (
                <div key={artifactId} className="mb-1">
                  <p className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    <FileText className="h-3 w-3" />
                    {group.artifactName}
                  </p>
                  {group.sections.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => pick(option)}
                      className="block w-full rounded-md px-2 py-1.5 text-left text-xs hover:bg-accent"
                    >
                      {option.sectionTitle}
                    </button>
                  ))}
                </div>
              ))}
              {filtered.length === 0 && (
                <p className="px-2 py-3 text-meta">No sections match.</p>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
